import { useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, Clock, Package, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
const Reservation = () => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    category: "",
    equipment: "",
    duration: "",
    description: ""
  });
  const {
    toast
  } = useToast();
  const equipmentCategories = [{
    id: "pack-location",
    name: "Pack Location",
    items: [
      "Pack événement complet", "Pack conférence", "Pack spectacle", "Pack mariage", 
      "Pack entreprise", "Pack sur mesure", "Pack sonorisation autonome", "Pack éclairage LED", 
      "Pack vidéo streaming", "Pack présentation", "Pack DJ mobile - 120.-", "Pack karaoké - 95.-", 
      "Pack conférence premium - 180.-", "Pack éclairage disco - 75.-", "Pack éclairage 1 - 75.-", 
      "Pack éclairage 2 - 75.-", "Pack éclairage 3 - 75.-", "Pack éclairage 4 - 75.-"
    ],
    images: ["/images/Capture d'écran 2023-05-08 à 09.14.48.png", "/images/Capture d'écran 2023-05-08 à 09.21.47.png", "/images/Capture d'écran 2023-05-08 à 10.04.57.png", "/images/Capture d'écran 2023-06-28 à 09.56.59.png"]
  }, {
    id: "sonorisation",
    name: "Sonorisation",
    items: ["Système de sonorisation portable", "Micros sans fil (HF)", "Table de mixage numérique", "Enceintes actives", "Amplificateurs", "Casques de monitoring"]
  }, {
    id: "video",
    name: "Vidéo",
    items: ["Écran LED géant", "Projecteur 4K", "Écran de projection", "Caméras de diffusion", "Régie vidéo mobile", "Système de streaming"]
  }, {
    id: "eclairage",
    name: "Éclairage",
    items: ["Projecteurs LED RGB", "Console d'éclairage DMX", "Structures d'éclairage", "Effets spéciaux", "Éclairage architectural", "Gradateurs de puissance"]
  }];
  const durations = ["1 jour", "2-3 jours", "1 semaine", "2 semaines", "1 mois", "Plus d'1 mois"];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !formData.name || !formData.email || !formData.category) {
      toast({
        title: "Informations manquantes",
        description: "Veuillez remplir tous les champs obligatoires.",
        variant: "destructive"
      });
      return;
    }
    toast({
      title: "Demande envoyée !",
      description: "Nous vous contacterons dans les plus brefs délais pour confirmer votre réservation."
    });

    // Reset form
    setFormData({
      name: "",
      email: "",
      phone: "",
      company: "",
      category: "",
      equipment: "",
      duration: "",
      description: ""
    });
    setSelectedDate(undefined);
  };
  const selectedCategory = equipmentCategories.find(cat => cat.id === formData.category);
  return <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-background to-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge variant="outline" className="mb-4">
              Réservation de Matériel
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">
              Réservez Votre Équipement
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Louez notre matériel professionnel pour vos événements, conférences et projets. 
              Équipements de qualité avec support technique inclus.
            </p>
          </div>
        </div>
      </section>

      {/* Pack Location Gallery */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Nos Packs Location</h2>
              <p className="text-lg text-muted-foreground">
                Découvrez nos équipements et installations professionnelles
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {equipmentCategories.find(cat => cat.id === 'pack-location')?.images?.map((image, index) => <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-video relative">
                    <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2000820534560542720_Capture d’écran 2023-05-08 à 09.48.30.png?image_process=quality,q_90/resize,w_1280/format,webp" alt={`Pack location ${index + 1}`} className="w-full sk-edit-loading h-[228.9375px] object-cover" />
                  </div>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground text-center">
                      {index === 0 ? "Sonorisation Autonome sur Batterie 450W avec 2 micro sans fil et Bluetooth + 1 pied - Prix 85.-" : `Équipement professionnel - Configuration ${index + 1}`}
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>
      </section>

      {/* Reservation Form */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Form */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl">Formulaire de Réservation</CardTitle>
                    <CardDescription>
                      Remplissez ce formulaire pour réserver votre matériel. Nous vous contacterons pour confirmer les détails.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Personal Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Nom complet *</Label>
                          <Input id="name" value={formData.name} onChange={e => setFormData({
                          ...formData,
                          name: e.target.value
                        })} placeholder="Votre nom complet" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input id="email" type="email" value={formData.email} onChange={e => setFormData({
                          ...formData,
                          email: e.target.value
                        })} placeholder="votre@email.ch" required />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Téléphone</Label>
                          <Input id="phone" value={formData.phone} onChange={e => setFormData({
                          ...formData,
                          phone: e.target.value
                        })} placeholder="026 660 45 88" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company">Entreprise</Label>
                          <Input id="company" value={formData.company} onChange={e => setFormData({
                          ...formData,
                          company: e.target.value
                        })} placeholder="Nom de votre entreprise" />
                        </div>
                      </div>

                      {/* Equipment Selection */}
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="category">Catégorie d'équipement *</Label>
                          <Select value={formData.category} onValueChange={value => setFormData({
                          ...formData,
                          category: value,
                          equipment: ""
                        })}>
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionnez une catégorie" />
                            </SelectTrigger>
                            <SelectContent>
                              {equipmentCategories.map(category => <SelectItem key={category.id} value={category.id}>
                                  {category.name}
                                </SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>

                        {selectedCategory && <div className="space-y-2">
                            <Label htmlFor="equipment">Équipement spécifique</Label>
                            <Select value={formData.equipment} onValueChange={value => setFormData({
                          ...formData,
                          equipment: value
                        })}>
                              <SelectTrigger>
                                <SelectValue placeholder="Sélectionnez un équipement" />
                              </SelectTrigger>
                              <SelectContent>
                                {selectedCategory.items.map((item, index) => <SelectItem key={index} value={item}>
                                    {item}
                                  </SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>}
                      </div>

                      {/* Date and Duration */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date de début *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-left font-normal">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {selectedDate ? format(selectedDate, "PPP", {
                                locale: fr
                              }) : "Sélectionner une date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} initialFocus locale={fr} disabled={date => date < new Date()} />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="duration">Durée de location</Label>
                          <Select value={formData.duration} onValueChange={value => setFormData({
                          ...formData,
                          duration: value
                        })}>
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionnez la durée" />
                            </SelectTrigger>
                            <SelectContent>
                              {durations.map(duration => <SelectItem key={duration} value={duration}>
                                  {duration}
                                </SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="space-y-2">
                        <Label htmlFor="description">Description du projet</Label>
                        <Textarea id="description" value={formData.description} onChange={e => setFormData({
                        ...formData,
                        description: e.target.value
                      })} placeholder="Décrivez votre événement ou projet (optionnel)" rows={4} />
                      </div>

                      <Button type="submit" size="lg" className="w-full">
                        Envoyer la demande de réservation
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Process */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Clock className="h-5 w-5" />
                      <span>Processus de Réservation</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</div>
                      <div>
                        <p className="font-medium">Demande</p>
                        <p className="text-sm text-muted-foreground">Remplissez le formulaire</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</div>
                      <div>
                        <p className="font-medium">Confirmation</p>
                        <p className="text-sm text-muted-foreground">Nous vous contactons sous 24h</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">3</div>
                      <div>
                        <p className="font-medium">Livraison</p>
                        <p className="text-sm text-muted-foreground">Installation et formation incluses</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Services Included */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Package className="h-5 w-5" />
                      <span>Services Inclus</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {["Livraison et installation", "Formation à l'utilisation", "Support technique 7/7", "Maintenance préventive", "Récupération du matériel"].map((service, index) => <div key={index} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span className="text-sm">{service}</span>
                      </div>)}
                  </CardContent>
                </Card>

                {/* Contact */}
                <Card>
                  <CardHeader>
                    <CardTitle>Besoin d'Aide ?</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Notre équipe est disponible pour vous conseiller dans le choix de votre matériel.
                    </p>
                    <Button variant="outline" className="w-full">
                      Nous Contacter
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
};
export default Reservation;